import { View } from "@novorender/api";

import { EventEmitter } from "utils/EventEmitter";

export type WatchdogWarmupReason = "primitive-stabilized" | "timer-fallback";

/**
 * - "underperformance": FPS below target despite the engine already rendering at the
 *   quality floor — the machine cannot keep up at all.
 * - "quality-floor": the engine holds the framerate target, but only by staying pinned
 *   at the quality floor — smooth but visually degraded (ghost-case signal).
 */
export type WatchdogTriggerMode = "underperformance" | "quality-floor";

export type WatchdogSnapshot = {
    /** Last-tick values (the 200 ms sample that crossed the threshold — may be unrepresentative). */
    fps: number;
    targetFps: number;
    resolution: number;
    detailBias: number;
    primitives: number;
    breach: boolean;
    breachRatio: number;
    qualityFloorRatio: number;
    sampleCount: number;
    requiredSamples: number;
    /** The resolution floor the trigger compared against (engine-derived when available). */
    resolutionFloorUsed: number;
    /** Aggregates over the rolling window that actually justified the trigger. */
    window: {
        fpsMedian: number;
        fpsMin: number;
        fpsMax: number;
        resolutionMedian: number;
        resolutionMin: number;
    };
};

export type WatchdogBreachEvent = {
    mode: WatchdogTriggerMode;
    snapshot: WatchdogSnapshot;
};

export type WatchdogWarmupEvent = {
    reason: WatchdogWarmupReason;
    elapsedMs: number;
};

export type WatchdogConfig = {
    sampleIntervalMs: number;
    windowSec: number;
    fpsRatio: number;
    breachRatio: number;
    /** Ratio of window samples that must be at the quality floor (with OK fps) to fire "quality-floor". */
    qualityFloorRatio: number;
    warmupSec: number;
    resolutionFloor: number;
    resolutionEpsilon: number;
    primitiveStableSamples: number;
    primitiveStableDelta: number;
};

export const defaultWatchdogConfig: WatchdogConfig = {
    sampleIntervalMs: 200,
    windowSec: 10,
    fpsRatio: 0.5,
    breachRatio: 0.8,
    qualityFloorRatio: 0.8,
    warmupSec: 20,
    resolutionFloor: 0.5,
    resolutionEpsilon: 0.02,
    primitiveStableSamples: 8,
    primitiveStableDelta: 1000,
};

type PerformanceWatchdogOptions = {
    view: View;
    internalEventEmitter: EventEmitter;
    framerateTarget: number;
    onBreach: (event: WatchdogBreachEvent) => void;
    onWarmupSettled?: (event: WatchdogWarmupEvent) => void;
    config?: Partial<WatchdogConfig>;
    /**
     * Returns the engine's actual dynamic-resolution floor (base render resolution ×
     * lowest DRS step). Takes precedence over `config.resolutionFloor` when provided;
     * called per tick since a canvas resize changes the floor.
     */
    getEngineResolutionFloor?: () => number | undefined;
};

export class PerformanceWatchdog {
    private readonly view: View;
    private readonly internalEventEmitter: EventEmitter;
    private readonly framerateTarget: number;
    private readonly onBreach: (event: WatchdogBreachEvent) => void;
    private readonly onWarmupSettled?: (event: WatchdogWarmupEvent) => void;
    private readonly config: WatchdogConfig;
    private readonly getEngineResolutionFloor?: () => number | undefined;

    private timer: ReturnType<typeof setInterval> | undefined;
    private warmupTimer: ReturnType<typeof setTimeout> | undefined;
    private moving = false;
    private firedUnderperformance = false;
    private firedQualityFloor = false;
    private warmupSettledAtMs: number | undefined;
    private startAtMs: number | undefined;
    private readonly samples: { breach: boolean; qualityFloor: boolean; fps: number; resolution: number }[] = [];
    private stablePrimitiveSampleCount = 0;
    private lastPrimitiveCount: number | undefined;
    private removeCameraUpdatedListener: (() => void) | undefined;
    private removeCameraSettledListener: (() => void) | undefined;

    constructor(options: PerformanceWatchdogOptions) {
        this.view = options.view;
        this.internalEventEmitter = options.internalEventEmitter;
        this.framerateTarget = options.framerateTarget;
        this.onBreach = options.onBreach;
        this.onWarmupSettled = options.onWarmupSettled;
        this.config = { ...defaultWatchdogConfig, ...options.config };
        this.getEngineResolutionFloor = options.getEngineResolutionFloor;
    }

    start() {
        if (this.timer) {
            return;
        }

        this.startAtMs = Date.now();
        this.warmupTimer = setTimeout(() => {
            if (this.warmupSettledAtMs !== undefined) {
                return;
            }
            this.warmupSettledAtMs = Date.now();
            this.onWarmupSettled?.({
                reason: "timer-fallback",
                elapsedMs: this.startAtMs !== undefined ? Date.now() - this.startAtMs : 0,
            });
        }, this.config.warmupSec * 1000);

        this.removeCameraUpdatedListener = this.internalEventEmitter.on("cameraUpdated", () => {
            this.moving = true;
        });
        this.removeCameraSettledListener = this.internalEventEmitter.on("cameraSettled", () => {
            this.moving = false;
        });

        this.timer = setInterval(() => {
            this.tick();
        }, this.config.sampleIntervalMs);
    }

    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
        if (this.warmupTimer) {
            clearTimeout(this.warmupTimer);
            this.warmupTimer = undefined;
        }
        this.removeCameraUpdatedListener?.();
        this.removeCameraSettledListener?.();
        this.removeCameraUpdatedListener = undefined;
        this.removeCameraSettledListener = undefined;
    }

    private tick() {
        if (this.firedUnderperformance && this.firedQualityFloor) {
            return;
        }

        if (!this.isWarmupSettled()) {
            return;
        }

        if (!this.moving) {
            return;
        }

        const stats = this.view.statistics;
        const frameInterval = stats?.render?.frameInterval;
        const resolution = stats?.view?.resolution;
        const detailBias = stats?.view?.detailBias;
        const primitives = stats?.render?.primitives;
        if (
            typeof frameInterval !== "number" ||
            frameInterval <= 0 ||
            typeof resolution !== "number" ||
            typeof detailBias !== "number" ||
            typeof primitives !== "number"
        ) {
            return;
        }

        const fps = 1000 / frameInterval;
        const requiredSamples = this.getRequiredSampleCount();
        const resolutionFloor = this.getEngineResolutionFloor?.() ?? this.config.resolutionFloor;
        const atFloor = resolution <= resolutionFloor + this.config.resolutionEpsilon;
        const fpsLow = fps < this.framerateTarget * this.config.fpsRatio;
        // Mutually exclusive per sample: the engine is either failing despite max
        // sacrifice (breach) or holding framerate only through max sacrifice (qualityFloor).
        const breach = atFloor && fpsLow;
        const qualityFloor = atFloor && !fpsLow;

        this.samples.push({ breach, qualityFloor, fps, resolution });
        if (this.samples.length > requiredSamples) {
            this.samples.shift();
        }

        if (this.samples.length < requiredSamples) {
            return;
        }

        const breachRatio = this.samples.filter((sample) => sample.breach).length / this.samples.length;
        const qualityFloorRatio = this.samples.filter((sample) => sample.qualityFloor).length / this.samples.length;

        const windowFps = this.samples.map((sample) => sample.fps);
        const windowResolutions = this.samples.map((sample) => sample.resolution);

        const snapshot: WatchdogSnapshot = {
            fps,
            targetFps: this.framerateTarget,
            resolution,
            detailBias,
            primitives,
            breach,
            breachRatio,
            qualityFloorRatio,
            sampleCount: this.samples.length,
            requiredSamples,
            resolutionFloorUsed: resolutionFloor,
            window: {
                fpsMedian: median(windowFps),
                fpsMin: Math.min(...windowFps),
                fpsMax: Math.max(...windowFps),
                resolutionMedian: median(windowResolutions),
                resolutionMin: Math.min(...windowResolutions),
            },
        };

        if (!this.firedUnderperformance && breachRatio >= this.config.breachRatio) {
            this.firedUnderperformance = true;
            this.onBreach({ mode: "underperformance", snapshot });
            return;
        }

        if (!this.firedQualityFloor && qualityFloorRatio >= this.config.qualityFloorRatio) {
            this.firedQualityFloor = true;
            this.onBreach({ mode: "quality-floor", snapshot });
        }
    }

    private isWarmupSettled(): boolean {
        if (this.warmupSettledAtMs !== undefined) {
            return true;
        }

        const elapsedMs = this.startAtMs !== undefined ? Date.now() - this.startAtMs : 0;
        const primitives = this.view.statistics?.render?.primitives;
        if (typeof primitives === "number") {
            if (this.lastPrimitiveCount === undefined) {
                this.lastPrimitiveCount = primitives;
            } else {
                const delta = Math.abs(primitives - this.lastPrimitiveCount);
                this.lastPrimitiveCount = primitives;
                if (delta <= this.config.primitiveStableDelta) {
                    this.stablePrimitiveSampleCount += 1;
                } else {
                    this.stablePrimitiveSampleCount = 0;
                }
            }
        }

        if (this.stablePrimitiveSampleCount >= this.config.primitiveStableSamples) {
            this.warmupSettledAtMs = Date.now();
            if (this.warmupTimer) {
                clearTimeout(this.warmupTimer);
                this.warmupTimer = undefined;
            }
            this.onWarmupSettled?.({ reason: "primitive-stabilized", elapsedMs });
            return true;
        }

        return false;
    }

    private getRequiredSampleCount(): number {
        const size = Math.round((this.config.windowSec * 1000) / this.config.sampleIntervalMs);
        return Math.max(1, size);
    }
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
